import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useAuth } from "./contexts/AuthContext";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import Layout from "./components/Layout/Layout";
import LandingPage from "./pages/LandingPage";
import Features from "./pages/Features";
// import Pricing from "./pages/Pricing"; // Temporarily disabled
import Security from "./pages/Security";
import About from "./pages/About";
import Contact from "./pages/Contact";
import HelpCenter from "./pages/HelpCenter";
import TrackTicket from "./pages/Support/TrackTicket";
import SupportTickets from "./pages/Support/SupportTickets";
import LoginPage from "./pages/Auth/LoginPage";
import RegisterPage from "./pages/Auth/RegisterPage";
import JobSeekerSignup from "./JobSeeker/pages/JobSeekerSignup";
// import PersonaVerificationPage from "./pages/PersonaVerificationPage";
import TermsAndConditions from "./pages/Legal/TermsAndConditions";
import PrivacyPolicy from "./pages/Legal/PrivacyPolicy";
import CancellationsRefunds from "./pages/Legal/CancellationsRefunds";
import ShippingPolicy from "./pages/Legal/ShippingPolicy";
import Dashboard from "./JobSeeker/pages/Dashboard";
import HRDashboard from "./HR/pages/Dashboard";
import AdminDashboard from "./pages/Admin/Dashboard";
import ResumeUpload from "./JobSeeker/pages/ResumeUpload";
import MyProfile from "./JobSeeker/pages/NewProfile";
import HRProfile from "./HR/pages/NewProfile";
import AvailableTests from "./JobSeeker/pages/AvailableTests";
import JDUpload from "./HR/pages/JDUpload";
import MyJobs from "./HR/pages/MyJobs";
import JobDetails from "./HR/pages/JobDetails";
import TestGenerator from "./HR/pages/TestGenerator";
import MyTests from "./HR/pages/MyTests";
import TakeTest from "./JobSeeker/pages/TakeTest";
import AIInterview from "./JobSeeker/pages/AIInterview";
import InterviewResults from "./JobSeeker/pages/InterviewResults";
import ScenarioTest from "./JobSeeker/pages/ScenarioTest";
import TestResults from "./JobSeeker/pages/TestResults";
import SkillsValidation from "./JobSeeker/pages/SkillsValidation";
import SkillsTestResults from "./JobSeeker/pages/SkillsTestResults";
import PersonalizedCourse from "./JobSeeker/pages/PersonalizedCourse";
import CourseTest from "./JobSeeker/pages/CourseTest";
import MyCourses from "./JobSeeker/pages/MyCourses";
import Analytics from "./JobSeeker/pages/Analytics";
import Applications from "./JobSeeker/pages/Applications";
import JobSeekerSubscription from "./JobSeeker/pages/Subscription";
import CandidateManagement from "./HR/pages/CandidateManagement";
import TestDetails from "./HR/pages/TestDetails";
import TestPreview from "./HR/pages/TestPreview";
import HRTestResults from "./HR/pages/TestResults";
import Shortlisted from "./HR/pages/Shortlisted";
import Rejected from "./HR/pages/Rejected";
import HRSubscription from "./HR/pages/Subscription";
import SharedTest from "./pages/SharedTest";
import UserManagement from "./pages/Admin/UserManagement";
import BadgeManagement from "./pages/Admin/BadgeManagement";
import MigrateTests from "./pages/Admin/MigrateTests";
import AdminPanel from "./pages/AdminPanel/AdminPanel";
import HROnboarding from "./HR/pages/HROnboarding";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import HRSubscriptionGuard from "./HR/components/HRSubscriptionGuard";
import ScrollToTop from "./components/ScrollToTop";
import ResumeGuard from "./components/ResumeGuard.jsx";
import { Toaster } from "react-hot-toast";

const AppRoutes = () => {
  const { user } = useAuth();

  // Enable idle timeout only for authenticated users
  // Auto-logout after 15 minutes of inactivity
  useIdleTimeout({
    timeout: 15 * 60 * 1000, // 15 minutes
  });

  // Determine what to show when users access the root path
  const getRootRedirect = () => {
    // If the visitor is not authenticated, show the public landing page
    if (!user) return <LandingPage />;

    // Otherwise, take them straight to the appropriate dashboard
    switch (user.userType) {
      case "jobseeker":
        return <Navigate to="/job-seeker" replace />;
      case "hr":
        return <Navigate to="/hr" replace />;
      case "admin":
        return <Navigate to="/admin" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={getRootRedirect()} />

      {/* Public Routes */}
      <Route path="/features" element={<Features />} />
      {/* <Route path="/pricing" element={<Pricing />} /> */} {/* Temporarily disabled */}
      <Route path="/security" element={<Security />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/help-center" element={<HelpCenter />} />
      <Route path="/track-ticket" element={<TrackTicket />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<RegisterPage />} />
      <Route path="/signup/jobseeker" element={<JobSeekerSignup />} />
      <Route path="/signup/hr" element={<Navigate to="/signup/jobseeker" replace />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route
        path="/terms-and-conditions"
        element={<TermsAndConditions />}
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route
        path="/cancellations-refunds"
        element={<CancellationsRefunds />}
      />
      <Route path="/test/share/:testId" element={<SharedTest />} />
      <Route path="/shipping" element={<ShippingPolicy />} />

      {/* Separate Admin Panel Route */}
      <Route path="/admin-panel" element={<AdminPanel />} />

      {/* Job Seeker Routes */}
      <Route
        path="/job-seeker"
        element={<Navigate to="/job-seeker/profile" replace />}
      />
      <Route
        path="/job-seeker/tests"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <AvailableTests />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/profile"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <Dashboard />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/my-profile"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <MyProfile />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/resume-upload"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <ResumeUpload />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* <Route
        path="/job-seeker/verify-identity"
        element={
          <ProtectedRoute userType="jobseeker">
            <PersonaVerificationPage />
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/job-seeker/take-test/:testId"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <TakeTest />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/ai-interview/:sessionId"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <AIInterview />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/interview-results/:sessionId"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <InterviewResults />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/scenario-test/:testId"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <ScenarioTest />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/results"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <TestResults />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/analytics"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <Analytics />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/applications"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <Applications />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/skills-validation"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <SkillsValidation />
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/skills-test-results/:testId"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <SkillsTestResults />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/my-courses"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <MyCourses />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/personalized-course/:courseId"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <PersonalizedCourse />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/course-test/:courseId"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <CourseTest />
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/subscription"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <JobSeekerSubscription />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/support"
        element={
          <ProtectedRoute userType="jobseeker">
            <ResumeGuard>
              <Layout userType="jobseeker">
                <SupportTickets />
              </Layout>
            </ResumeGuard>
          </ProtectedRoute>
        }
      />

      {/* HR Routes */}
      {/* HR Onboarding - No subscription check */}
      <Route
        path="/hr/onboarding"
        element={
          <ProtectedRoute userType="hr">
            <HROnboarding />
          </ProtectedRoute>
        }
      />

      {/* HR Dashboard and other routes - Require subscription */}
      <Route
        path="/hr"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <HRDashboard />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/profile"
        element={<Navigate to="/hr" replace />}
      />
      <Route
        path="/hr/my-profile"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <HRProfile />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/support"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <SupportTickets />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/jd-upload"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <JDUpload />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/my-jobs"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <MyJobs />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/my-jobs/:jobId"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <JobDetails />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/test-generator"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <TestGenerator />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/my-tests"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <MyTests />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/my-tests/:testId"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <TestDetails />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/test-preview"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <TestPreview />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/candidates"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <CandidateManagement />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/test-results/:testId"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <HRTestResults />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/shortlisted"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <Shortlisted />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/rejected"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <Rejected />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/subscription"
        element={
          <ProtectedRoute userType="hr">
            <Layout userType="hr">
              <HRSubscription />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute userType="admin">
            <Layout userType="admin">
              <AdminDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute userType="admin">
            <Layout userType="admin">
              <UserManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/badges"
        element={
          <ProtectedRoute userType="admin">
            <Layout userType="admin">
              <BadgeManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/migrate-tests"
        element={
          <ProtectedRoute userType="admin">
            <Layout userType="admin">
              <MigrateTests />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
function App() {
  // Global Security (Disable Right-Click, Copy/Paste, Shortcuts)
  useEffect(() => {
    // 1. Disable Right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    // 2. Disable Copy/Cut/Paste events
    const handleCopyPaste = (e) => {
      e.preventDefault();
      return false;
    };

    // 3. Disable Keyboard Shortcuts (Ctrl+C, Ctrl+V, etc.)
    const handleKeyDown = (e) => {
      if (!e.key) return;
      const key = e.key.toLowerCase();
      const isCtrl = e.ctrlKey || e.metaKey;
      const isShift = e.shiftKey;

      // Block Ctrl+Shift+I (Dev Tools)
      if (isCtrl && isShift && key === 'i') {
        e.preventDefault();
        return false;
      }

      // Check for Ctrl/Cmd key
      if (isCtrl) {
        // Block: C, V, X, A (Select All), S (Save), P (Print)
        if (['c', 'v', 'x', 'a', 's', 'p'].includes(key)) {
          e.preventDefault();
          return false;
        }
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("copy", handleCopyPaste);
    document.addEventListener("cut", handleCopyPaste);
    document.addEventListener("paste", handleCopyPaste);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("copy", handleCopyPaste);
      document.removeEventListener("cut", handleCopyPaste);
      document.removeEventListener("paste", handleCopyPaste);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <div className="min-h-screen bg-neutral-100 text-neutral-800 overflow-x-hidden">
            <ScrollToTop />
            <AppRoutes />
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3000,
                style: {
                  borderRadius: "24px",
                  background: "#FFFFFF",
                  border: "1px solid rgba(209, 213, 219, 0.6)",
                  color: "#1F2937",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  marginTop: "80px", // Account for sticky navigation
                },
                success: {
                  iconTheme: {
                    primary: "#059669",
                    secondary: "#ECFDF5",
                  },
                },
                error: {
                  iconTheme: {
                    primary: "#E11D48",
                    secondary: "#FFF1F2",
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

